import { useTranslation } from "react-i18next"

import type { DocumentReferencePreview } from "@/api/models"

import { ChevronRight } from "@/components/icons"
import { sanitizeSnippetForPreview } from "@/features/chat/utils/document-reference-preview-utils"
import { cn } from "@/lib/utils"

type DocumentChangeType = NonNullable<DocumentReferencePreview["change_type"]>
type DocumentReferencePreviewCardProps = {
  reference: DocumentReferencePreview
  changeTypes?: DocumentChangeType[]
  onOpen: (documentId: string) => void
}

const supportedChangeTypes = new Set<DocumentChangeType>([
  "created",
  "updated",
  "edited",
  "read",
  "searched",
])

function normalizeChangeType(
  changeType: DocumentReferencePreview["change_type"] | undefined
): DocumentChangeType {
  if (!changeType || !supportedChangeTypes.has(changeType)) {
    return "updated"
  }

  return changeType === "edited" ? "updated" : changeType
}

function formatReferenceTimestamp(updatedAt?: string): string | null {
  if (!updatedAt) {
    return null
  }

  const parsedDate = new Date(updatedAt)
  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsedDate)
}

export function DocumentReferencePreviewCard({
  reference,
  changeTypes,
  onOpen,
}: DocumentReferencePreviewCardProps) {
  const { t } = useTranslation()
  const badgeLabelByChangeType = {
    created: t("documents.created", { defaultValue: "Created" }),
    edited: t("documents.updated", { defaultValue: "Updated" }),
    read: t("documents.read", { defaultValue: "Read" }),
    searched: t("documents.searched", { defaultValue: "Searched" }),
    updated: t("documents.updated", { defaultValue: "Updated" }),
  } as const
  const resolvedChangeTypes = (
    changeTypes?.length ? changeTypes : [reference.change_type ?? "updated"]
  ).map((changeType) => normalizeChangeType(changeType))
  const uniqueChangeTypes = Array.from(new Set(resolvedChangeTypes))
  const badgeLabel = uniqueChangeTypes
    .map((changeType) => badgeLabelByChangeType[changeType])
    .join(" \u2022 ")
  const sanitizedSnippet = sanitizeSnippetForPreview(reference.snippet)
  const formattedTimestamp = formatReferenceTimestamp(reference.updated_at)

  return (
    <button
      type="button"
      className={cn(
        "group/card border-border/45 bg-background/10 hover:border-border/70 hover:bg-background/18 relative w-full cursor-pointer rounded-xl border p-3 text-left transition-colors duration-200"
      )}
      onClick={() => onOpen(reference.document_id)}
    >
      <div className="flex items-start gap-3 pr-5">
        <div
          aria-hidden="true"
          className="hidden h-[106px] w-[74px] shrink-0 sm:flex sm:items-center sm:justify-center"
        >
          <div className="border-border/50 bg-background relative h-[102px] w-[70px] overflow-hidden rounded-[4px] border p-1.5 shadow-xs">
            <span
              aria-hidden="true"
              className="bg-muted/45 border-border/45 absolute top-0 right-0 h-2.5 w-2.5 rounded-bl-[3px] border-b border-l"
            />
            <p className="line-clamp-2 text-[4px] leading-[1.2] font-semibold tracking-tight">
              {reference.title}
            </p>
            <p className="text-muted-foreground mt-1 line-clamp-14 text-[3px] leading-[1.2]">
              {sanitizedSnippet || reference.title}
            </p>
          </div>
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="text-foreground truncate text-sm font-medium">{reference.title}</p>
          <div className="text-muted-foreground flex items-center gap-1 text-xs">
            <span>{badgeLabel}</span>
            {formattedTimestamp && (
              <>
                <span aria-hidden="true">&bull;</span>
                <span>{formattedTimestamp}</span>
              </>
            )}
          </div>
          <p className="text-muted-foreground line-clamp-2 text-xs leading-relaxed">
            {sanitizedSnippet ||
              t("documents.preview_not_available", {
                defaultValue: "Document preview not available",
              })}
          </p>
        </div>
      </div>
      <ChevronRight
        aria-hidden="true"
        className="text-muted-foreground/60 pointer-events-none absolute top-1/2 right-3 size-3.5 -translate-y-1/2 opacity-0 transition-[opacity,translate] duration-200 group-hover/card:translate-x-0.5 group-hover/card:opacity-100"
      />
    </button>
  )
}
